import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import IconButton from '@mui/material/IconButton';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import ActivateIcon from '@mui/icons-material/CheckCircle';
import SearchIcon from '@mui/icons-material/Search';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import { fetchData, deleteData, activateData } from '../../../apiService';
import { useAuth } from '../../../AuthContext';
import { tokenIsExpired } from '../../../authUtils';
import './Role.css';

function RoleIndex() {
  const { isAuthenticated, companyId, permissions, token, logout } = useAuth();
  const [data, setData] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  const [deleteItemId, setDeleteItemId] = useState(null);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);

  const hasEditAdministrationPermission = permissions.includes('Edit Administration');
  const hasDeleteAdministrationPermission = permissions.includes('Delete Administration');

  useEffect(() => {
    if (isAuthenticated && tokenIsExpired(token)) { logout(); return; }
    fetchtableData();
  }, [isAuthenticated, companyId, token]);

  const fetchtableData = () => {
    fetchData('RoleApi', `/${companyId}`, token)
      .then((response) => {
        if (response.responseCode === '1111') setData(response.response_data);
      })
      .catch((e) => console.error('Error fetching Role Details:', e));
  };

  const filteredData = data.filter((item) =>
    Object.values(item).some((v) => String(v).toLowerCase().includes(searchQuery.toLowerCase()))
  );

  const handleDelete = () => {
    deleteData('RoleApi', `${deleteItemId}`, token)
      .then(() => { fetchtableData(); setDeleteItemId(null); setConfirmDialogOpen(false); })
      .catch((e) => console.error('Error deleting record:', e));
  };

  const handleActivate = (itemId) => {
    activateData('RoleApi', `${itemId}`, token)
      .then(() => fetchtableData())
      .catch(() => {});
  };

  return (
    <div className="role-container">
      <div className="role-header-card">
        <div className="role-header-content">
          <div className="role-title-section">
            <span className="role-title-icon"><AdminPanelSettingsIcon fontSize="small" /></span>
            <h1 className="role-title">Roles</h1>
          </div>
          <div className="role-header-actions">
            <div className={'role-search-wrapper' + (searchOpen ? ' open' : '')}>
              <span className="role-search-icon" onClick={() => setSearchOpen((o) => !o)}>
                <SearchIcon fontSize="small" />
              </span>
              {searchOpen && (
                <input
                  autoFocus
                  className="role-search-input"
                  type="text"
                  placeholder="Search..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onBlur={() => { if (!searchQuery) setSearchOpen(false); }}
                />
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="role-table-wrapper">
        <TableContainer component={Paper} className="role-table-container" elevation={0}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell className="role-th">Sl.No</TableCell>
                <TableCell className="role-th">Role Name</TableCell>
                <TableCell className="role-th" align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredData.map((item, index) => (
                <TableRow key={item.Id} className={'role-row' + (!item.IsActive ? ' role-row-inactive' : '')}>
                  <TableCell className="role-td">{index + 1}</TableCell>
                  <TableCell className="role-td">{item.Name}</TableCell>
                  <TableCell className="role-td" align="center">
                    <div className="role-action-btns">
                      {hasEditAdministrationPermission && (
                        <Link to={`/roleDetail/${item.Id}`}>
                          <IconButton className="role-icon-btn role-icon-edit" size="small" title="Edit">
                            <EditIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Link>
                      )}
                      {hasDeleteAdministrationPermission && (
                        <IconButton
                          className={'role-icon-btn ' + (item.IsActive ? 'role-icon-delete' : 'role-icon-activate')}
                          size="small"
                          title={item.IsActive ? 'Delete' : 'Activate'}
                          onClick={() => item.IsActive ? (setDeleteItemId(item.Id), setConfirmDialogOpen(true)) : handleActivate(item.Id)}
                        >
                          {item.IsActive ? <DeleteIcon sx={{ fontSize: 16 }} /> : <ActivateIcon sx={{ fontSize: 16 }} />}
                        </IconButton>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </div>

      <Dialog open={confirmDialogOpen} onClose={() => setConfirmDialogOpen(false)} PaperProps={{ style: { borderRadius: '10px', padding: '4px' } }}>
        <DialogTitle style={{ fontSize: '16px', fontWeight: 600 }}>Delete Record?</DialogTitle>
        <DialogContent>
          <DialogContentText style={{ fontSize: '13px' }}>Are you sure you want to delete this record?</DialogContentText>
        </DialogContent>
        <DialogActions style={{ padding: '12px 20px', gap: '8px' }}>
          <button className="rol-btn rol-btn-cancel" onClick={() => setConfirmDialogOpen(false)}>Cancel</button>
          <button className="rol-btn rol-btn-delete" onClick={handleDelete}>Delete</button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

export default RoleIndex;
